/**
 * Filters for camper-facing cabin lists.
 *
 * CampMinder exposes "LODGE" and "KITCHEN" as bunks/areas that hold staff,
 * not campers. Sync ingests them faithfully, but they must never appear in
 * the cabin grid, division headers, camper counts, or reports that
 * counselors and read-only users see. Admins still need to see them in
 * Manage Cabins and sync-debugging surfaces, so this filter is opt-in:
 * apply it on camper-facing pages, leave it off on admin pages.
 */
import { parseCabinNumber } from "./terminology";

export interface CabinLike {
  name: string | null | undefined;
  isStaffHousing?: boolean | null;
}

/**
 * True if a cabin should appear on a camper-facing page. A camper cabin is
 * either a numbered cabin (1-26) or the special "Rangers" cabin. Anything
 * flagged as staff housing is excluded outright, as is any non-numeric
 * label like "LODGE" or "KITCHEN".
 */
export function isCamperCabin(cabin: CabinLike): boolean {
  if (cabin.isStaffHousing) return false;
  const name = cabin.name == null ? "" : String(cabin.name).trim();
  if (!name) return false;
  if (parseCabinNumber(name) != null) return true;
  if (/^rangers$/i.test(name)) return true;
  return false;
}

/** Convenience filter for arrays of cabins. */
export function filterCamperCabins<T extends CabinLike>(cabins: readonly T[]): T[] {
  return cabins.filter(isCamperCabin);
}
